import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Link,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Tooltip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { differenceInCalendarDays, parseISO, isValid } from "date-fns";
import {
  buildOndemandLink,
  siteTargetEnvironment,
} from "configs/products";

const EXTRA_BRANCH_FIELDS = [
  "be_custom_branch",
  "fe_branch",
  "fe_custom_branch",
];

const STATUS_COLORS = {
  creating: "info",
  complete: "success",
  error: "error",
  deleted: "default",
};

const emailLocalPart = (email) => {
  if (!email || typeof email !== "string") {
    return "—";
  }
  return email.split("@")[0] || email;
};

const formatCreated = (createdAt) => {
  if (!createdAt) {
    return "—";
  }
  // Naive "YYYY-MM-DD HH:MM:SS" — show the date part.
  return String(createdAt).slice(0, 10);
};

const formatExpires = (expiryDate) => {
  if (!expiryDate) {
    return { label: "—", daysLabel: null };
  }
  const parsed = parseISO(String(expiryDate).slice(0, 10));
  if (!isValid(parsed)) {
    return { label: String(expiryDate), daysLabel: null };
  }
  const days = differenceInCalendarDays(parsed, new Date());
  let daysLabel;
  if (days < 0) {
    daysLabel = "expired";
  } else if (days === 0) {
    daysLabel = "today";
  } else if (days === 1) {
    daysLabel = "1 day";
  } else {
    daysLabel = `${days} days`;
  }
  return { label: String(expiryDate).slice(0, 10), daysLabel };
};

const branchExtraCount = (site) =>
  EXTRA_BRANCH_FIELDS.filter((field) => Boolean(site[field])).length;

const truncateError = (error, max = 80) => {
  if (!error) {
    return "";
  }
  const text = String(error);
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

const BRANCH_LABEL_MAX = 30;

const truncateBranch = (branch) => {
  const text = String(branch);
  if (text.length <= BRANCH_LABEL_MAX) {
    return text;
  }
  return `${text.slice(0, BRANCH_LABEL_MAX)}...`;
};

const siteLabel = (site) =>
  site?.org_subdomain || site?.subdomain || site?.hostname || "this site";

const SitesTable = ({
  sites,
  emptyMessage = null,
  onDecommission,
  decommissioningSk = null,
}) => {
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [menuSite, setMenuSite] = React.useState(null);
  const [confirmSite, setConfirmSite] = React.useState(null);

  const openMenu = (event, site) => {
    setMenuAnchor(event.currentTarget);
    setMenuSite(site);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuSite(null);
  };

  const siteUrl = menuSite ? buildOndemandLink(menuSite) : "";

  const handleOpenSite = () => {
    if (siteUrl) {
      window.open(siteUrl, "_blank", "noopener,noreferrer");
    }
    closeMenu();
  };

  const handleCopyUrl = async () => {
    if (siteUrl && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(siteUrl);
      } catch {
        // Ignore clipboard failures in non-secure contexts.
      }
    }
    closeMenu();
  };

  const handleDecommissionClick = () => {
    setConfirmSite(menuSite);
    closeMenu();
  };

  const closeConfirm = () => {
    setConfirmSite(null);
  };

  const handleConfirmDecommission = () => {
    if (confirmSite && onDecommission) {
      onDecommission(confirmSite);
    }
    setConfirmSite(null);
  };

  const decommissionDisabled =
    !menuSite?.SK ||
    menuSite.state === "deleted" ||
    Boolean(decommissioningSk);

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="On-demand sites">
          <TableHead>
            <TableRow>
              <TableCell>Site</TableCell>
              <TableCell>Environment</TableCell>
              <TableCell>Branch</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell align="right" width={48} />
            </TableRow>
          </TableHead>
          <TableBody>
            {sites.length === 0 && emptyMessage ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" color="textSecondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
            {sites.map((site) => {
              const url = buildOndemandLink(site);
              const env = siteTargetEnvironment(site);
              const extras = branchExtraCount(site);
              const expires = formatExpires(site.expiry_date);
              const canLink = site.state === "complete" && Boolean(url);

              return (
                <TableRow key={site.SK} hover>
                  <TableCell>
                    <Typography variant="subtitle2" component="div">
                      {site.org_subdomain || site.subdomain || "—"}
                    </Typography>
                    {canLink ? (
                      <Link
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        underline="hover"
                      >
                        {site.hostname || url.replace(/^https:\/\//, "")}
                      </Link>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {site.hostname || "—"}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {env ? (
                      <Chip size="small" label={env} variant="outlined" />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{ gap: 0.5 }}
                    >
                      {site.be_branch ? (
                        <Tooltip
                          title={
                            String(site.be_branch).length > BRANCH_LABEL_MAX
                              ? site.be_branch
                              : ""
                          }
                        >
                          <Chip
                            size="small"
                            label={truncateBranch(site.be_branch)}
                            variant="outlined"
                          />
                        </Tooltip>
                      ) : (
                        "—"
                      )}
                      {extras > 0 ? (
                        <Tooltip
                          title={[
                            site.be_custom_branch &&
                              `be_custom: ${site.be_custom_branch}`,
                            site.fe_branch && `fe: ${site.fe_branch}`,
                            site.fe_custom_branch &&
                              `fe_custom: ${site.fe_custom_branch}`,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        >
                          <Chip size="small" label={`+${extras}`} />
                        </Tooltip>
                      ) : null}
                    </Box>
                  </TableCell>
                  <TableCell>{emailLocalPart(site.email)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={site.state || "—"}
                      color={STATUS_COLORS[site.state] || "default"}
                      variant={site.state === "deleted" ? "outlined" : "filled"}
                    />
                    {site.state === "error" && site.error ? (
                      <Typography
                        variant="caption"
                        color="error"
                        display="block"
                        title={String(site.error)}
                      >
                        {truncateError(site.error)}
                      </Typography>
                    ) : null}
                    {site.SK === decommissioningSk ? (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                      >
                        decommissioning
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{formatCreated(site.created_at)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" component="div">
                      {expires.label}
                    </Typography>
                    {expires.daysLabel ? (
                      <Typography variant="caption" color="textSecondary">
                        {expires.daysLabel}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label="Site actions"
                      onClick={(event) => openMenu(event, site)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
      >
        <MenuItem onClick={handleOpenSite} disabled={!siteUrl}>
          Open site
        </MenuItem>
        <MenuItem onClick={handleCopyUrl} disabled={!siteUrl}>
          Copy URL
        </MenuItem>
        <MenuItem
          onClick={handleDecommissionClick}
          disabled={decommissionDisabled}
        >
          Decommission
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(confirmSite)}
        onClose={closeConfirm}
        aria-labelledby="decommission-dialog-title"
      >
        <DialogTitle id="decommission-dialog-title">
          Decommission site
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Decommission {siteLabel(confirmSite)}? This tears down the
            on-demand site and its database. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Cancel</Button>
          <Button
            onClick={handleConfirmDecommission}
            color="error"
            variant="contained"
            disableElevation
          >
            Decommission
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SitesTable;
